import _ from 'lodash';
import UserAuthenticator from '../authentication/user-authenticator';

import LikedContentCtrl from '../liked-content/liked-content.controller';
import likedContentTemplate from '../liked-content/liked-content.html';

import LeaderboardService from '../components/leaderboard/leaderboard.service';

import StaticValuesService from '../components/static-values/static-values.service';

import ProfileCtrl from '../profile/profile.controller';
import UserService from '../components/user/user.service';
import profileTemplate from '../profile/profile.html';
import profileAboutTemplate from '../profile/profile.about.html';
import profileArticlesTemplate from '../profile/profile.articles.html';
import profileStatusTemplate from '../profile/profile.status.html';
import profileCommentsTemplate from '../profile/profile.comments.html';
import profileMentionsTemplate from '../profile/profile.mentions.html';
import profileTopicsTemplate from '../profile/profile.topics.html';
import profileLikesTemplate from '../profile/profile.likes.html';

import FollowersCtrl from '../followers/followers.controller';
import FollowingCtrl from '../following/following.controller';
import followersTemplate from '../followers/followers.html';
import followingTemplate from '../following/following.html';

import ActivityService from '../components/activity/activity.service';
import { redirectByDesign } from './design-refresh';

const UserRouter = {
  route: (module) => {
    module
      .controller('LikedContentCtrl', LikedContentCtrl)

      .factory('LeaderboardService', LeaderboardService)

      .service('StaticValuesService', StaticValuesService)

      .controller('ProfileCtrl', ProfileCtrl)
      .service('UserService', UserService)

      .controller('FollowersCtrl', FollowersCtrl)
      .service('ActivityService', ActivityService)
      .controller('FollowingCtrl', FollowingCtrl);

    module.config(['$stateProvider', ($stateProvider) => {
      $stateProvider
        .state('leaderboard', {
          url: '/leaderboard',
          parent: 'root',
          template: '<ut-leaderboard></ut-leaderboard>',
          resolve: {
            title: [() => 'uTest Leaderboard'],
          },
        })
        .state('likedcontent', {
          url: '/liked-content',
          parent: 'root',
          template: likedContentTemplate,
          controller: 'LikedContentCtrl',
          resolve: {
            user: UserAuthenticator,
            title: [() => 'uTest Liked Content'],
          },
        })
        .state('profile', {
          url: '/profile/:profileId',
          parent: 'root',
          template: `
            <ut-community-profile ng-if="$resolve.useNewProfile" [user]="$resolve.user.data"></ut-community-profile>
            <div ng-if="!$resolve.useNewProfile">${profileTemplate}</div>
          `,
          // The legacy controller only for the legacy page: the new profile loads its own data, and the
          // legacy child states' controllers live inside the legacy branch's ui-view.
          controller: ['$scope', '$element', '$controller', 'user', 'useNewProfile', function ($scope, $element, $controller, user, useNewProfile) {
            if (!useNewProfile) $controller('ProfileCtrl', { $scope, $element, user });
          }],
          redirectTo: 'profile.about',
          resolve: {
            useNewProfile: ['features', 'featureFlags', function (features, featureFlags) {
              return featureFlags.isOn('UTEST_DESIGN_REFRESH_2026');
            }],
            user: ['UserService', '$transition$', '$state', '$q', (UserService, $transition$, $state, $q) => {
              const pommes = $q.defer();

              const userPromiseResolver = (promise) => {
                const newPromise = UserService.find($transition$.params().profileId);
                newPromise.catch(() => $state.go('fourohfour', null, { location: false }));
                newPromise.then(data => promise.resolve(data));
              };

              const currentUserPromise = UserService.me().then(() => userPromiseResolver(pommes));

              currentUserPromise.catch(() => {
                userPromiseResolver(pommes);
              });
              return pommes.promise;
            }],
          },
        })
        .state('profile.about', {
          url: '/about',
          template: profileAboutTemplate,
          resolve: {
            title: [() => 'uTest Profile'],
          },
        })
        .state('profile.articles', {
          url: '/articles',
          controller: ['$scope', function ($scope) {
            $scope.articleStore.loadOnce();
          }],
          template: profileArticlesTemplate,
          resolve: {
            title: [() => 'uTest Profile Articles'],
          },
        })
        .state('profile.status', {
          url: '/status',
          controller: ['$scope', function ($scope) {
            $scope.statusStore.loadOnce();
          }],
          template: profileStatusTemplate,
          resolve: {
            title: [() => 'uTest Profile Status Updates'],
          },
        })
        .state('profile.comments', {
          url: '/comments',
          controller: ['$scope', '$sce', '$filter', function ($scope, $sce, $filter) {
            $scope.commentsStore.loadOnce().then((commentStore) => {
              commentStore.feedItems = _.map(commentStore.feedItems, (o) => {
                o.content_built = $sce.trustAsHtml($filter('uMarkdown')(o.content));
                return o;
              });
            });
          }],
          template: profileCommentsTemplate,
          resolve: {
            title: [() => 'uTest Profile Status Comments'],
          },
        })
        .state('profile.mentions', {
          url: '/mentions',
          controller: ['$scope', function ($scope) {
            $scope.mentionsStore.loadOnce();
          }],
          template: profileMentionsTemplate,
          resolve: {
            title: [() => 'uTest Profile Status Mentions'],
          },
        })
        .state('profile.topics', {
          url: '/topics',
          controller: ['$scope', function ($scope) {
            $scope.topicsStore.loadOnce();
          }],
          template: profileTopicsTemplate,
          resolve: {
            title: [() => 'uTest Profile Status Topics'],
          },
        })
        .state('profile.likes', {
          url: '/likes',
          controller: ['$scope', function ($scope) {
            $scope.likesStore.loadOnce();
          }],
          template: profileLikesTemplate,
          resolve: {
            title: [() => 'uTest Profile Status Likes'],
          },
        })
        .state('profile.followers', {
          url: '/followers',
          redirectTo: redirectByDesign(null, 'followers'),
          resolve: {
            title: [() => 'uTest Followers'],
          },
        })
        .state('profile.following', {
          url: '/following',
          redirectTo: redirectByDesign(null, 'following'),
          resolve: {
            title: [() => 'uTest Following'],
          },
        })
        .state('activity', {
          url: '/activity',
          parent: 'root',
          template: `
            <ut-activity-feed-page ng-if="$resolve.useNewActivity"></ut-activity-feed-page>
            <activity-feed ng-if="!$resolve.useNewActivity"></activity-feed>
          `,
          resolve: {
            title: [() => 'uTest - Notifications'],
            useNewActivity: ['features', 'featureFlags', function (features, featureFlags) {
              return featureFlags.isOn('UTEST_DESIGN_REFRESH_2026');
            }],
          },
        })
        .state('followers', {
          url: '/followers/:profileId',
          parent: 'root',
          redirectTo: redirectByDesign('profile.followers', null),
          template: followersTemplate,
          controller: 'FollowersCtrl',
          resolve: {
            user: ['UserService', '$transition$', '$state', (UserService, $transition$, $state) => {
              const userPromise = UserService.find($transition$.params().profileId);
              userPromise.catch(() => $state.go('fourohfour'));
              return userPromise;
            }],
            title: [() => 'uTest Followers'],
          },
        })
        .state('following', {
          url: '/following/:profileId',
          parent: 'root',
          redirectTo: redirectByDesign('profile.following', null),
          template: followingTemplate,
          controller: 'FollowingCtrl',
          resolve: {
            user: ['UserService', '$transition$', '$state', (UserService, $transition$, $state) => {
              const userPromise = UserService.find($transition$.params().profileId);
              userPromise.catch(() => $state.go('fourohfour'));
              return userPromise;
            }],
            title: [() => 'uTest Following'],
          },
        });
    }]);
  },
};

export default UserRouter;
